import React, { useState, useEffect } from "react";
import { Download, Share, Copy, XLogo, FacebookLogo, InstagramLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareCardModal({ open, onClose, pr }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pr) return;
    setLoading(true);
    setImgUrl(null);
    setBlobUrl(null);

    api
      .get(`/prs/${pr.pr_id}/card`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        setImgUrl(url);
        setBlobUrl(url);
      })
      .catch(() => toast.error("Could not generate card"))
      .finally(() => setLoading(false));

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pr]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `edge-training-pr-${pr.exercise.replace(/\s+/g, "-").toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Card downloaded");
  };

  const shareText = pr
    ? `🔥 New PR! ${pr.exercise}: ${pr.value}${pr.unit} — built with @Edge TrainingApp`
    : "";

  const shareNative = async () => {
    if (!blobUrl) return;
    try {
      const blob = await (await fetch(blobUrl)).blob();
      const file = new File([blob], "edge-training-pr.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "New PR on Edge Training",
          text: shareText,
          files: [file],
        });
        return;
      }
    } catch (e) {
      // fall through
    }
    toast.info("Native share unavailable. Download and post manually.");
  };

  const copyImage = async () => {
    if (!blobUrl) return;
    try {
      const blob = await (await fetch(blobUrl)).blob();
      await navigator.clipboard.write([
        new window.ClipboardItem({ "image/png": blob }),
      ]);
      toast.success("Image copied. Paste into any post.");
    } catch {
      toast.error("Couldn't copy. Try Download instead.");
    }
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(shareText);
    toast.success("Caption copied");
  };

  const tweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const facebook = () => {
    // Facebook requires a URL; download is the better path. Open a quote share with site URL.
    const siteUrl = window.location.origin;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-surface-base border-white/10 text-white max-w-2xl p-0 overflow-hidden" data-testid="share-card-modal">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Share size={20} weight="duotone" className="text-brand-accent" /> SHARE YOUR PR
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6">
          {/* Card preview */}
          <div className="relative bg-black border border-white/10 mb-5 overflow-hidden" style={{ aspectRatio: "1080/1350" }}>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="label-tiny text-text-secondary">RENDERING...</div>
              </div>
            )}
            {imgUrl && (
              <img src={imgUrl} alt="PR card" className="w-full h-full object-contain animate-fade-up" data-testid="share-card-preview" />
            )}
          </div>

          {/* Caption preview + copy */}
          {pr && (
            <div className="border border-white/10 p-3 mb-4 flex items-start justify-between gap-3 bg-surface-DEFAULT">
              <div className="flex-1 text-sm text-text-secondary">{shareText}</div>
              <button
                onClick={copyCaption}
                className="text-text-secondary hover:text-brand-accent flex-shrink-0"
                data-testid="copy-caption-button"
                title="Copy caption"
              >
                <Copy size={16} />
              </button>
            </div>
          )}

          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              onClick={download}
              disabled={!blobUrl}
              className="btn-kinetic py-3 text-xs label-tiny tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="download-card-button"
            >
              <Download size={14} weight="bold" /> DOWNLOAD
            </button>
            <button
              onClick={shareNative}
              disabled={!blobUrl}
              className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base py-3 text-xs label-tiny tracking-widest transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="share-native-button"
            >
              <Share size={14} weight="bold" /> SHARE
            </button>
          </div>

          {/* Social platform buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={tweet}
              disabled={!blobUrl}
              className="border border-white/10 hover:border-white/40 py-2.5 text-xs label-tiny tracking-wider transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="share-twitter-button"
            >
              <XLogo size={13} weight="bold" /> X / TWITTER
            </button>
            <button
              onClick={copyImage}
              disabled={!blobUrl}
              className="border border-white/10 hover:border-white/40 py-2.5 text-xs label-tiny tracking-wider transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="share-instagram-button"
              title="Copy image — paste into Instagram"
            >
              <InstagramLogo size={13} weight="bold" /> IG (COPY)
            </button>
            <button
              onClick={facebook}
              disabled={!blobUrl}
              className="border border-white/10 hover:border-white/40 py-2.5 text-xs label-tiny tracking-wider transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="share-facebook-button"
            >
              <FacebookLogo size={13} weight="bold" /> FACEBOOK
            </button>
          </div>

          <p className="text-xs text-text-secondary text-center mt-4">
            Tip: download → post to Instagram Story. The card is 1080×1350 (IG portrait).
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
